import { constructCurrentDate } from "./utils";
import { appName, getRequest, postRequest } from "./apimanager.helper";

export default class APIManager {
  static api_url =
    window.location.pathname.includes("prodreader.html") ||
    window.location.pathname.includes("prod_index.html")
      ? process.env.API_ENDPOINT_PROD
      : process.env.API_ENDPOINT;

  static getUserProfileValue(type) {
    let url = `https://${this.api_url}/Data/DataService.svc/GetProfileValues/${type}`;
    return getRequest(url);
  }

  static getRedirectorUrl(type, version, userId, downloadToken) {
    // neede to add a trailing slash due to some API strangeness on https it
    // redirects to https with a 307 in case the / is not there
    let url = `https://${this.api_url}/Redirector/RedirectorService.svc/GetRedirection/${version}/webapp/${userId}/${type}/`;

    const promise = new Promise((resolve, reject) => {
      // if the redirection url exist, then we get a 200
      // if it does not exist we get a 404
      fetch(url)
        .then((resp) => {
          if (resp && resp.ok) {
            // we resolve with the original url, since on IE and older version of
            // chrome and firefox and opera the resp.url is not filled in, but this
            // one is correct since the redirects ended up here
            resolve(url);
          } else {
            reject("No url available");
          }
        })
        .catch(() => {
          // in case the url exists and it redirects, it might give a
          // cors error due to the fact that the url will be on the customer
          // domain, we can only hit this case if a url was defined, since otherwise
          // the API would have returned a 404, so we consider the original url
          // valid
          if (downloadToken) {
            url = `${url}${downloadToken}/`;
          }
          resolve(url);
        });
    });
    return promise;
  }

  static getRegions() {
    return getRequest(
      `https://${this.api_url}/Data/DataService.svc/GetProfileValues/Regio`
    );
  }

  static getProfileValues(value) {
    return getRequest(
      `https://${this.api_url}/Data/DataService.svc/GetProfileValues/${value}`
    );
  }

  static getContentPackagePublications(contentPackageId) {
    return getRequest(
      `https://${this.api_url}/data/dataService.svc/GetContentPackagePublications-${contentPackageId}-V3.json`
    );
  }

  static getNonExpiredShelfPublications(downloadToken) {
    return getRequest(
      `https://${this.api_url}/data/dataService.svc/GetNonExpiredShelfPublications/${downloadToken}?format=json`
    );
  }

  static getContentPackageList(downloadToken, number = 30, api) {
    return getRequest(
      `https://${
        api ? api : this.api_url
      }/Data/DataService.svc/getcontentpackagelist/${downloadToken}/0/${number}`
    );
  }

  static getPromospot(downloadToken) {
    return getRequest(
      `https://${this.api_url}/api/data/KiosquePublications/${downloadToken}?format=json`
    );
  }
  // eslint-disable-next-line camelcase
  static openSession(version, userId, OS_Version, UUID) {
    // eslint-disable-next-line camelcase
    return getRequest(
      `https://${this.api_url}/Session/SessionService.svc/json/OpenSession/${appName}/${userId}/${UUID}/${OS_Version}/${version}`
    );
  }

  static getAdditionalDownloadTokens() {
    return getRequest(
      `https://${this.api_url}/Data/DataService.svc/GetProfileValues/AdditionalDownloadTokens`
    );
  }

  static validateSubscriptionToken(
    userId,
    sessionId,
    ssoToken,
    downloadToken,
    subsType
  ) {
    return getRequest(
      `https://${
        this.api_url
      }/Session/SessionService.svc/json/ValidateSubscriptionToken?UserID=${userId}&SessionID=${sessionId}&subscriptionToken=${encodeURIComponent(
        ssoToken
      )}&downloadToken=${downloadToken}&subscriptionType=${subsType}`
    );
  }

  static validateSubscription(
    sessionId,
    downloadToken,
    userName,
    password,
    subscriptionType,
    version,
    userId,
    authenticationProvider
  ) {
    return postRequest(
      `https://${this.api_url}/Session/SessionService.svc/json/Validate_Subscription`,
      {
        Device: appName,
        DownloadToken: downloadToken,
        Email: userName,
        Password: password,
        SessionId: sessionId,
        UserId: userId,
        SubscriptionType: subscriptionType,
        AuthenticationProvider: authenticationProvider,
        Version: version,
      }
    );
  }

  static getContentPackageInfo(contentPackageId) {
    return postRequest(
      `https://${this.api_url}/Data/DataService.svc/GetContentPackageInfo`,
      { ContentPackageId: contentPackageId }
    );
  }

  static requestDownload(
    userId,
    sessionId,
    subsId,
    cpId,
    orderId,
    paymentMethod
  ) {
    return getRequest(
      `https://${this.api_url}/Session/SessionService.svc/json/RequestDownload/${userId}/${sessionId}/${subsId}/${cpId}/${orderId}/${paymentMethod}`
    );
  }

  static requestOrderPost(userId, sessionId, cpId, version, paymentMethod) {
    const postData = {
      UserId: userId,
      SessionId: sessionId,
      ContentPackageId: cpId,
      PaymentMethod: paymentMethod,
      Version: version,
      Device: appName,
    };
    return postRequest(
      `https://${this.api_url}/Session/SessionService.svc/json/Request_Order`,
      postData
    );
  }

  static confirmDownload({
    userId,
    sessionId,
    contentPackageId,
    downloadId,
    downloadStatus,
  }) {
    const postData = {
      UserId: userId,
      SessionId: sessionId,
      Version: process.env.PLATFORM_VERSION,
      DownloadPublicationStatusHistory: {
        PublicationId: contentPackageId,
        PublicationQuality: "Full", //TODO: amways full?
        RequestedPublicationTitleFormat: "Newspaper", //TODO: viewer: currentViewer === 'nextgen' ? 'TabletOptimized' : 'Newspaper',
        StatusInfo: "",
        StatusTime: constructCurrentDate(),
      },
      DownloadId: downloadId,
      Device: "WebApp",
      DownloadStatus: downloadStatus,
    };
    return postRequest(
      `https://${this.api_url}/Session/SessionService.svc/json/Confirm_Download`,
      postData
    );
  }

  static requestFullPDFDownload(
    userId,
    sessionId,
    orderId = 0,
    subsId = 0,
    cpId,
    pubId
  ) {
    const postfix = pubId ? `/${pubId}.json` : ".json";
    return getRequest(
      `https://${
        this.api_url
      }/api/downloadservice/requestfullpdfdownload/${userId}/${sessionId}/${orderId}/${subsId}/${cpId}${postfix}?_=${Date.now()}`
    );
  }

  static retreivePdfUrl(pageId, sessionId, userId, downloadId) {
    return new Promise((resolve, reject) => {
      fetch(
        `https://${this.api_url}/Pdf/${pageId}/${process.env.PLATFORM_VERSION}/${sessionId}/${userId}/${downloadId}/url`
      )
        .then((resp) => {
          return resp.json();
        })
        .then((json) => {
          resolve(json.ReturnMessage);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  static getDownloadCredentials(userId, sessionId, downloadId) {
    const url = `https://${this.api_url}/GetDownloadCredentials/${userId}/${sessionId}/${downloadId}`;
    return fetch(url);
  }
}



// WEBPACK FOOTER //
// ./src/common/utilities/apimanager.js